import React, { useState, useRef, useEffect, FormEvent } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import arrowOutlined from "@/assets/arrow-outlined.svg";
import { Url } from "@/helpers/url";
import { useServices } from "@/services-context";
import styles from "./login-page.module.scss";
import fonts from "@/style/fonts.module.scss";
import EnterHandler from "@/elements-react/enter-handler";
import { FooterElement } from "@/elements-react/footer-element/FooterElement";
import { FixLink } from "@/elements-react/fix-link";
import { FixButton } from "@/elements-react/fix-button";
import { Input } from "@/uikit";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";
import { Gtag } from "@/services/gtag.ts";
import { pageSuspense, useSuspenseLoader } from "@/helpers/suspense";
import { Services } from "@/services/services";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import { googleEvent } from "@/helpers/google-events.ts";
import { DocumentElements } from "@/elements-react/document-elements/document-elements";
import { SeoMarkupDataset } from "@/services/seo-markup.ts";
import { PageTags } from "@/elements-react/document-elements/page-tags.tsx";
import {Header} from "@/elements-react/catalog-layout/header";

const fetchPageData = async (params: { services: Services; location: LocationHook, route: RouteHook }) => {
  const { services, location, route } = params;

  await services.initialize.prepare({
    location: location,
    route: route,
  });

  const registryUrl = Url.normalizeRoute(route.path);
  const alternates = await services.routes.resolveAlternates(registryUrl);

  return {
    type: "static",
    canonical: registryUrl,
    alternates: alternates,
  };
};

const LoginPage = pageSuspense(() => {
  const route = useRoute();
  const location = useLocation();
  const services = useServices();

  const seoMarkupDataset = useSuspenseLoader({
    key: "login-route",
    loader: () =>
      fetchPageData({
        services: services,
        location: location,
        route: route,
      }),
  }) as SeoMarkupDataset;

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const formRef = useRef<HTMLFormElement>(null);
  const { layoutType, defaultLayoutStyleType } = useLayoutType();

  useEffect(() => {
    if (services.customerChannel?.customer) {
      redirectToHomePage();
    }
    // eslint-disable-next-line
  }, [services.customerChannel?.customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;

    let error = "";

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        error = t("x926694e5jfby7au.email-incorrect", "Please enter a valid email address");
      }
    }

    setFormErrors((prev) => ({ ...prev, [name]: error }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClear = (name: keyof typeof formData) => {
    setFormData((prev) => ({ ...prev, [name]: "" }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    const { email, password } = formData;
    if (!email || !password) return;

    setLoading(true);
    const loginResult = await services.customerChannel.login({
      email,
      password,
    });
    setLoading(false);

    if (!loginResult.success) {
      setFormErrors({ email: loginResult.errors[0] ?? t("jwr35j86u25g5ej6.default-error", "Incorrect username or password") });
      return;
    }
    await services.customerChannel.login({ email, password });
    await services.customerChannel.fetchCustomerChannel();
    services.cart.fetchCart();

    Gtag?.pushEvent?.("login_click", "");

    if (services.customerChannel.customer?.isBusinessCabinetAllowed) {
      redirectToB2bCabinet();
      return;
    }

    redirectToHomePage();
  };

  const redirectToHomePage = () => {
    Url.route(Url.to("home-page-with-lang"));
  };

  const redirectToB2bCabinet = () => {
    Url.navigateOutside("/business-v2");
  };

  const additionalLink = Url.to("forgot-password-page");

  return (
    <div className={classNames(styles.root)}>
      <Header simple={true}/>

      <div className={classNames(styles[defaultLayoutStyleType])}>
        <div className={classNames(styles.loginPageContainer)}>
          <div className={styles.loginContainer}>
            <EnterHandler onEnter={handleSubmit}>
              <form className={styles.loginForm} name="login" ref={formRef} onSubmit={handleSubmit}>
                <div>
                  <h1 className={classNames(styles.loginHeading, fonts["heading-2"])}>{t("7e19h22khwn365zm.sign-in", "Sign In")}</h1>
                </div>
                <div className={styles.inputsContainer}>
                  <Input
                    id="email-input"
                    name="email"
                    type="email"
                    placeholder={t("stci3dd82scs5j9p.your-email", "* Your email")}
                    customError={formErrors.email}
                    required={true}
                    clearable={true}
                    value={formData.email}
                    onChange={handleChange}
                    onInput={handleChange}
                    onClear={() => handleClear("email")}
                  />
                  <Input
                    name="password"
                    type="password"
                    placeholder={t("n6udbn26p3xrl83d.password", "* Password")}
                    // helpText={t("3t8k9e9jqwp1pn82.please-enter-password", "Please enter password")}
                    customError={formErrors.password}
                    required={true}
                    value={formData.password}
                    onChange={handleChange}
                    clearable={true}
                    onClear={() => handleClear("password")}
                  />
                </div>

                <FixButton onClick={handleSubmit} variant="primary" type="submit" fullWidth={true} loading={loading}>
                  {t("8wzxb3kopj9y3659.login", "Login")}
                </FixButton>
              </form>
            </EnterHandler>

            <div className={styles.divider}></div>

            <div className={styles.additionalBlock}>
              <span className={classNames(styles.firstLine)}>{t("s0hdh7oc8j01ydvf.forgot-password-text", "Forgot your password?")}</span>

              <FixLink
                className={styles.additionalLink}
                href={additionalLink}
                type={LayoutStyleTypes.TYPE_SM}
                onClick={() => googleEvent("forgot_password_click")}
              >
                {t("44by1up0qxwa3clu.click-here", "Click here")}
              </FixLink>
            </div>
          </div>
        </div>
      </div>

      {layoutType === LayoutTypes.TYPE_DESKTOP && <FooterElement />}
      <DocumentElements />
      <PageTags seo={seoMarkupDataset} />
    </div>
  );
});

export { LoginPage };
